import React, { ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Label } from 'scorer-ui-kit';
import styled, { css } from 'styled-components';

const ColorRangeWrapper = styled.div`
  width: 100%;
`;

const ColorRangeSelector = styled.div`
  height: 100px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 35px 0;
`;

const LabelUserSpecified = styled(Label)`
  margin: 12px 0 12px;
`;

const NumberInputWrapper = styled.div`
  width: 100%;
  display: flex;
  gap: 0 20px;
  align-items: center;
  justify-content: space-between;
`;

const NumberInputLabelContainer = styled.div`
  display: flex;
  align-items: center;
`;

const NumberInputLabel = styled(Label)`
  padding-right: 5px;
  margin: 0;
  & > span {
    margin: 0;
  }
`;

const LabelHue = styled(NumberInputLabel)`
  marginRight: 25px;
`;

const NumberInput = styled.input`
  width: 50px;
  color: #8d9397;
  border: 1px solid #8d9397;
  outline: none;
`;

const ColorRangeSlider = styled.div`
  position: relative;
  width: 100%;
`;

const SliderLabelContainer = styled.div`
  position: relative;
`;

const LabelC = styled.label`
  font-size: 18px;
  position: absolute;
  top: -32px;
  color: black;
`;

const SliderStyle = css`
  appearance: none;
  position: absolute;
  margin: 0;
  width: 100%;
  height: 40px;
  background: none transparent;
  cursor: pointer;
  pointer-events: none;

  &::-webkit-slider-runnable-track {
    appearance: none;
  }
  &::-moz-range-track {
    appearance: none;
  }
  &::-webkit-slider-thumb {
    pointer-events: all;
    position: relative;
    appearance: none;
    width: 4px;
    height: 55px;
    border-radius: 5px;
    border: none;
    background: rgb(0, 0, 0);
    cursor: pointer;
  }
  &::-moz-range-thumb {
    pointer-events: all;
    position: fixed;
    appearance: none;
    width: 4px;
    height: 55px;
    border-radius: 5px;
    border: none;
    background: rgb(0, 0, 0);
    cursor: pointer;
  }
`;

const StyledDatalist = styled.datalist`
  display: flex;
  position: absolute;
  justify-content: space-between;
  width: 100%;
  top: 50px;
  font-size: 10px;
`;

const SliderX = styled.input`
  ${SliderStyle};
`;

const SliderY = styled.input`
  ${SliderStyle};
`;

const highlighter = css`
  height: 40px;
  left: 0;
  position: absolute;
  background-image: linear-gradient(
    to right,
    hsl(0, 100%, 50%),
    hsl(60, 100%, 50%),
    hsl(120, 100%, 50%),
    hsl(180, 100%, 50%),
    hsl(240, 100%, 50%),
    hsl(300, 100%, 50%),
    hsl(360, 100%, 50%)
  );
`;
const FullRange = styled.span`
  width: 100%;
  ${highlighter};
`;
const InclRange = styled.span`
  ${highlighter};
  background: rgba(255,255,255,.8);
`;
const InclRange1 = styled.span`
  ${highlighter};
  background: rgba(255,255,255,.8);
`;
const InclRange2 = styled.span`
  ${highlighter};
  background: rgba(255,255,255,.8);
`;

interface props {
  hueSelect(hueX: number|null, hueY: number|null): void,
  hueX: number,
  hueY: number
}

export default function ColorSlider({hueSelect, hueX, hueY}: props): ReactElement {
  // states
  const [sliderXvalue, setSliderXvalue] = useState<number>(hueX);
  const [sliderYvalue, setSliderYvalue] = useState<number>(hueY);
  // refs
  const sliderXLabelRef = useRef<any>(null);
  const sliderYLabelRef = useRef<any>(null);
  const whenXgYref = useRef<HTMLElement | null>(null); // refs span for condition (X > Y)
  const whenYgXref1 = useRef<HTMLElement | null>(null); // refs span for condition (X < Y)
  const whenYgXref2 = useRef<HTMLElement | null>(null); // refs span for condition (X < Y)
  // hooks
  const { t } = useTranslation(['Common']);

  // calculates position of label
  useEffect(() => {
    let inputMinRange = 0;
    let thumbWidth = 4;
    let sliderRange = 179;

    let positionX = ((sliderXvalue - inputMinRange) / sliderRange) * 100;
    let positionOffsetX =
      Math.round((thumbWidth * positionX) / 100) - thumbWidth / 2;

    let positionY = ((sliderYvalue - inputMinRange) / sliderRange) * 100;
    let positionOffsetY =
      Math.round((thumbWidth * positionY) / 100) - thumbWidth / 2;

    if (sliderXLabelRef.current
      && sliderYLabelRef.current
      && whenXgYref.current
      && whenYgXref1.current
      && whenYgXref2.current
    ) {
      sliderXLabelRef.current.style.left = `calc(${positionX}% - ${positionOffsetX}px + -4.5px)`;
      sliderYLabelRef.current.style.left = `calc(${positionY}% - ${positionOffsetY}px + -4.5px)`;
      if(sliderXvalue > sliderYvalue){
        whenXgYref.current.style.visibility = 'visible';
        whenYgXref1.current.style.visibility = 'hidden';
        whenYgXref2.current.style.visibility = 'hidden';       
        whenXgYref.current.style.left = sliderYvalue * 100/179 + '%';      
        whenXgYref.current.style.width = (sliderXvalue-sliderYvalue) * 100/179 + '%';        
      } else {
        whenXgYref.current.style.visibility = 'hidden';
        whenYgXref1.current.style.visibility = 'visible';
        whenYgXref2.current.style.visibility = 'visible';
        whenYgXref1.current.style.width = sliderXvalue * 100/179 + '%';
        whenYgXref2.current.style.left = `calc(${positionY}% - ${positionOffsetY}px + -4.5px + 4.875px)`;
        whenYgXref2.current.style.right = '0';
      }
    }
  }, [sliderXvalue, sliderYvalue]);

  useEffect(()=>{
    hueSelect(sliderXvalue,sliderYvalue);
  },[sliderXvalue, sliderYvalue,hueSelect]);

  const onInputNumber = useCallback((e:any,valType:string)=>{
    if(valType === 'X'){
      if(e.target.value < 0 || e.target.value === ''){
        setSliderXvalue(0);
      } else if(e.target.value > 179){
        setSliderXvalue(179);
      } else {
        setSliderXvalue(parseInt(e.target.value));
      }
    } else if(valType === 'Y'){
      if(e.target.value < 0 || e.target.value === ''){
        setSliderYvalue(0);
      } else if(e.target.value > 179){
        setSliderYvalue(179);
      } else {
        setSliderYvalue(parseInt(e.target.value));
      }
    }
  },[]);

  return (
    <ColorRangeWrapper>
      <LabelUserSpecified htmlFor='' labelText={t('Common:color.userSpecified')} />
      <ColorRangeSelector>
        <NumberInputWrapper>
          <LabelHue htmlFor='' labelText={t('hue')+' :'} />
          <NumberInputLabelContainer>
            <NumberInputLabel htmlFor='Xvalue' labelText='X' />
            <NumberInput
              type='number'
              name='XValue'
              min='0'
              max='179'
              value={sliderXvalue}
              onChange={(e)=>onInputNumber(e,'X')}
            />
          </NumberInputLabelContainer>
          <NumberInputLabelContainer>
            <NumberInputLabel htmlFor='YValue' labelText='Y' />
            <NumberInput
              type='number'
              name='YValue'
              min='0'
              max='179'
              value={sliderYvalue}
              onChange={(e)=>onInputNumber(e,'Y')}
            />
          </NumberInputLabelContainer>
        </NumberInputWrapper>
        <ColorRangeSlider>
          <FullRange />
          <InclRange ref={whenXgYref} />
          <InclRange1 ref={whenYgXref1} id='whenYgXref1' />
          <InclRange2 ref={whenYgXref2} />
          <SliderLabelContainer>
            <LabelC id='labelX' htmlFor='sliderX' ref={sliderXLabelRef}>
              x
            </LabelC>
            <SliderX
              min='0'
              max='179'
              step='1'
              type='range'
              id='sliderX'
              value={sliderXvalue}
              onChange={(e) => setSliderXvalue(parseInt(e.target.value))}
            />
          </SliderLabelContainer>
          <SliderLabelContainer>
            <LabelC id='labelY' htmlFor='sliderY' ref={sliderYLabelRef}>
              y
            </LabelC>
            <SliderY
              min='0'
              max='179'
              step='1'
              type='range'
              id='sliderY'
              value={sliderYvalue}
              onChange={(e) => setSliderYvalue(parseInt(e.target.value))}
            />
            <StyledDatalist id='scale'>
              <option>0</option>
              <option>60</option>
              <option>120</option>
              <option>179</option>
            </StyledDatalist>
          </SliderLabelContainer>
        </ColorRangeSlider>
      </ColorRangeSelector>
    </ColorRangeWrapper>
  );
}
